import { shuffle } from "lodash";
import { useCallback } from "react";

import { matchPath, matchPlayerPath } from "../firestorePaths";

import { SEAT_POSITIONS } from "../constants";
import { Game, Match, MatchPlayer } from "../types/firestoreTypes";
import { useFirestore } from "react-redux-firebase";
import { useSelector } from "react-redux";
import {
  selectMe,
  selectMatch,
  selectPlayersForMatch,
  selectMatchGames,
  selectMatchPlayers as selectAllMatchPlayers,
} from "../store/selectors";
import { useCurrentUser } from "./useCurrentUser";
import { createGame } from "../helpers/createGame";

// Original hook for specific match (used by MatchListing)
interface MatchHookReturnType {
  match: Match | undefined;
  matchListing: Match | undefined;
  join: () => Promise<void>;
  joined: boolean;
  games: Game[] | undefined;
  matchPlayers: MatchPlayer[] | undefined;
}

export const useMatchById = ({ matchId }: { matchId: string }): MatchHookReturnType => {
  const firestore = useFirestore();
  const match = useSelector(selectMatch(matchId));
  const matchListing = useSelector(selectMatch(matchId));
  const matchPlayers = useSelector(selectPlayersForMatch(matchId));
  const games = useSelector(selectMatchGames(matchId));
  const currentUser = useSelector(selectMe);
  const { setCurrentMatchId } = useCurrentUser();
  console.log("useMatch", { matchPlayers });

  const joined =
    matchPlayers?.find((p) => p.id === currentUser?.id) !== undefined;

  const join = useCallback(async () => {
    if (currentUser === undefined) {
      throw new Error("No current user found; failed to join Match");
    }
    if (joined) {
      setCurrentMatchId(matchId);
      return;
    }

    console.log("useMatch.join", { matchPlayers });

    const occupiedSeats = matchPlayers?.map(({ seat }) => seat);
    const availableSeats = SEAT_POSITIONS.filter(
      (s) => !occupiedSeats?.includes(s)
    );

    if (availableSeats.length === 0) {
      throw new Error("all seats occupied: failed to join Match");
    }

    const path = matchPlayerPath({ matchId, playerId: currentUser.id });
    const matchPlayer: MatchPlayer = {
      matchId,
      id: currentUser.id,
      human: true,
      name: currentUser.name,
      seat: shuffle(availableSeats)[0],
    };
    await firestore.set(path, matchPlayer);

    setCurrentMatchId(matchId);
  }, [
    matchPlayers,
    currentUser,
    firestore,
    matchId,
    joined,
    setCurrentMatchId,
  ]);

  return {
    match,
    matchListing,
    games,
    matchPlayers,
    join,
    joined,
  };
};

// For backwards compatibility
export const useMatch = useMatchById;

// New hook for match actions without requiring a specific matchId upfront
interface UseMatchActionsReturnType {
  joinMatch: (matchId: string, reservedSeat?: number) => Promise<MatchPlayer>;
  leaveMatch: (matchId: string) => Promise<void>;
  startGameForMatch: (matchId: string, players: MatchPlayer[]) => Promise<void>;
}

export const useMatchActions = (): UseMatchActionsReturnType => {
  const firestore = useFirestore();
  const currentUser = useSelector(selectMe);
  const { setCurrentMatchId, clearCurrentMatchId } = useCurrentUser();
  const allMatchPlayers = useSelector(selectAllMatchPlayers);

  const joinMatch = useCallback(
    async (matchId: string, reservedSeat?: number) => {
      if (currentUser === undefined) {
        throw new Error("No current user found; failed to join Match");
      }

      // Check if already joined using redux state
      const matchPlayers = allMatchPlayers?.filter((p) => p.matchId === matchId) ?? [];
      const alreadyJoined = matchPlayers.some((p) => p.id === currentUser.id);

      let humanPlayer: MatchPlayer;
      
      if (!alreadyJoined) {
        const occupiedSeats = matchPlayers.map(({ seat }) => seat);
        const availableSeats = SEAT_POSITIONS.filter(
          (s) => !occupiedSeats.includes(s)
        );

        // Use reserved seat if provided, otherwise pick randomly from available
        let chosenSeat: number;
        if (reservedSeat !== undefined && availableSeats.includes(reservedSeat)) {
          chosenSeat = reservedSeat;
        } else if (availableSeats.length > 0) {
          chosenSeat = shuffle(availableSeats)[0];
        } else {
          throw new Error("All seats occupied: failed to join Match");
        }
        
        const path = matchPlayerPath({ matchId, playerId: currentUser.id });
        humanPlayer = {
          matchId,
          id: currentUser.id,
          human: true,
          name: currentUser.name,
          seat: chosenSeat,
        };
        await firestore.set(path, humanPlayer);
      } else {
        // Already joined - find the existing player
        humanPlayer = matchPlayers.find((p) => p.id === currentUser.id)!;
      }

      setCurrentMatchId(matchId);
      return humanPlayer;
    },
    [currentUser, firestore, setCurrentMatchId, allMatchPlayers]
  );

  const leaveMatch = useCallback(
    async (matchId: string) => {
      if (currentUser === undefined) return;

      // Remove player from match
      const path = matchPlayerPath({ matchId, playerId: currentUser.id });
      await firestore.delete(path);

      clearCurrentMatchId();
    },
    [currentUser, firestore, clearCurrentMatchId]
  );

  const startGameForMatch = useCallback(
    async (matchId: string, matchPlayers: MatchPlayer[]) => {
      if (matchPlayers.length !== 4) {
        throw new Error("Need exactly 4 players to start");
      }

      // Clear the waiting room flag
      const matchDocPath = matchPath({ matchId });
      await firestore.update(matchDocPath, { isWaitingRoom: false });

      // Create the first game
      await createGame({
        firestore,
        matchId,
        players: matchPlayers,
        gameIndex: 0,
      });
    },
    [firestore]
  );

  return {
    joinMatch,
    leaveMatch,
    startGameForMatch,
  };
};
